import React, { useState, useEffect, useRef, useCallback } from 'react';
import { waivers } from '../services/api';

export default function WaiverPage() {
  const [waiver, setWaiver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('read'); // read → form → confirm
  const [form, setForm] = useState({ guest_name: '', guest_email: '', guest_phone: '', typed_name_consent: '' });
  const [signatureData, setSignatureData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Signature canvas
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  useEffect(() => {
    waivers.getActive()
      .then(setWaiver)
      .catch(() => setError('Unable to load waiver. Please try again later.'))
      .finally(() => setLoading(false));
  }, []);

  // Canvas drawing handlers
  const startDraw = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    isDrawingRef.current = true;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const draw = useCallback((e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineTo(x, y);
    ctx.stroke();
  }, []);

  const endDraw = useCallback(() => {
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL('image/png'));
    }
  }, []);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  const handleSubmit = async () => {
    if (!form.guest_name.trim() || !form.guest_email.trim() || !form.typed_name_consent.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!signatureData) {
      setError('Please draw your signature below.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await waivers.sign({
        ...form,
        waiver_version_id: waiver.id,
        signature_data: signatureData,
      });
      setResult(res);
      setStep('confirm');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading waiver...</div>;

  return (
    <div className="waiver-page">
      <div className="waiver-container">
        <div className="waiver-header">
          <h1>🥾 LCPOA Community Trails</h1>
          <p>Guest Waiver</p>
        </div>

        {error && !result && (
          <div className="alert alert-error">{error}<button onClick={() => setError(null)}>✕</button></div>
        )}

        {/* ── Step 1: Read Waiver ────────────────────────────── */}
        {step === 'read' && waiver && (
          <>
            <div className="waiver-text" dangerouslySetInnerHTML={{ __html: waiver.content }} />
            <div className="waiver-version">Version {waiver.version_number}</div>
            <button className="btn-primary btn-block" onClick={() => setStep('form')}>
              I Have Read the Waiver — Continue to Sign
            </button>
          </>
        )}

        {/* ── Step 2: Sign Form ──────────────────────────────── */}
        {step === 'form' && (
          <div className="waiver-form">
            <h2>Guest Information</h2>

            <label>
              Full Name <span className="required">*</span>
              <input type="text" value={form.guest_name}
                onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                placeholder="Your full legal name" />
            </label>

            <label>
              Email Address <span className="required">*</span>
              <input type="email" value={form.guest_email}
                onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                placeholder="email@example.com" />
            </label>

            <label>
              Phone Number
              <input type="tel" value={form.guest_phone}
                onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                placeholder="(555) 123-4567" />
            </label>

            <h2>Consent</h2>

            <label>
              Type your full name to consent <span className="required">*</span>
              <input type="text" value={form.typed_name_consent}
                onChange={(e) => setForm({ ...form, typed_name_consent: e.target.value })}
                placeholder="Type your full name exactly as above" />
            </label>

            <div className="signature-section">
              <p>Draw your signature below <span className="required">*</span></p>
              <canvas
                ref={canvasRef}
                width={360} height={120}
                className="signature-canvas"
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
              />
              <button className="btn-sm btn-secondary" onClick={clearSignature}>Clear Signature</button>
            </div>

            <div className="waiver-form-actions">
              <button className="btn-secondary" onClick={() => setStep('read')}>Back</button>
              <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Sign Waiver'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Confirmation ───────────────────────────── */}
        {step === 'confirm' && result && (
          <div className="waiver-confirm">
            <div className="confirm-icon">✅</div>
            <h2>Waiver Signed Successfully</h2>
            <p>Thank you, <strong>{result.guest_name}</strong>.</p>
            <p>A confirmation email has been sent to your email address.</p>
            <div className="confirm-details">
              <p><strong>Signed:</strong> {new Date(result.signed_at).toLocaleString()}</p>
              <p><strong>Waiver Version:</strong> {result.waiver_version}</p>
              <p><strong>Reference:</strong> {result.signature_id}</p>
            </div>
            <p className="confirm-note">You may now close this page and enjoy the La Cresta trails. Please follow all posted trail rules.</p>
          </div>
        )}

        {!waiver && !loading && (
          <div className="waiver-error">
            <p>No active waiver is currently available. Please contact the LCPOA Trails Committee.</p>
          </div>
        )}
      </div>
    </div>
  );
}
